import type { AppView } from '../../app/navigation';
import { isAppView } from '../../app/navigation';
import { parsePrettyTaskPath } from '../../utils/prettyRoutes';
import { calendarEventTargetFromUrl, normalizeCalendarEventTarget } from '../calendar/calendarEventNavigation';

export interface NotificationNavigationMetadata {
  eventId?: string | null;
  occurrenceStart?: string | null;
  chatId?: string | null;
  messageId?: string | null;
  taskId?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  projectTab?: string | null;
}

export type NotificationNavigationIntent =
  | { kind: 'calendar-event'; eventId: string; occurrenceStart?: string }
  | { kind: 'boq-comment'; url: string }
  | { kind: 'chat'; chatId: string; messageId?: string }
  | { kind: 'task'; taskId?: string }
  | { kind: 'project'; projectId: string; projectName?: string; projectTab?: string }
  | { kind: 'view'; view: AppView }
  | { kind: 'none' };

function parseUrlTarget(url?: string): { pathname: string; searchParams: URLSearchParams } | null {
  if (!url || typeof url !== 'string') return null;

  try {
    const parsed = new URL(url, window.location.origin);
    return {
      pathname: parsed.pathname,
      searchParams: parsed.searchParams,
    };
  } catch {
    return null;
  }
}

export function resolveNotificationNavigationIntent(
  url?: string,
  metadata?: NotificationNavigationMetadata
): NotificationNavigationIntent {
  const eventTarget = (metadata?.eventId
    ? normalizeCalendarEventTarget(metadata.eventId, metadata.occurrenceStart)
    : null) ?? (url ? calendarEventTargetFromUrl(url) : null);
  if (eventTarget) return { kind: 'calendar-event', ...eventTarget };
  const commentTarget = parseUrlTarget(url);
  if (commentTarget && (commentTarget.pathname.includes('/pricing/') || commentTarget.pathname === '/projects')
    && commentTarget.searchParams.has('boqProjectId')
    && commentTarget.searchParams.has('boqDocumentId')
    && commentTarget.searchParams.has('boqItemId')
    && commentTarget.searchParams.has('boqCommentId')) {
    return { kind: 'boq-comment', url: `${commentTarget.pathname}?${commentTarget.searchParams}` };
  }
  const chatId = typeof metadata?.chatId === 'string' && metadata.chatId.length > 0 ? metadata.chatId : null;
  if (chatId) {
    return {
      kind: 'chat',
      chatId,
      messageId:
        typeof metadata?.messageId === 'string' && metadata.messageId.length > 0
          ? metadata.messageId
          : undefined,
    };
  }

  const taskId =
    typeof metadata?.taskId === 'string' && metadata.taskId.length > 0 ? metadata.taskId : null;
  if (taskId) return { kind: 'task', taskId };

  const projectId =
    typeof metadata?.projectId === 'string' && metadata.projectId.length > 0 ? metadata.projectId : null;
  if (projectId) {
    return {
      kind: 'project',
      projectId,
      projectName:
        typeof metadata?.projectName === 'string' && metadata.projectName.length > 0
          ? metadata.projectName
          : undefined,
      projectTab:
        typeof metadata?.projectTab === 'string' && metadata.projectTab.length > 0
          ? metadata.projectTab
          : undefined,
    };
  }

  const parsed = parseUrlTarget(url);
  if (!parsed) {
    return { kind: 'none' };
  }

  // Push links use the same identified task paths as the address bar. Resolve
  // them before bare views so /tasks?taskId also keeps its exact destination.
  try {
    const taskRoute = parsePrettyTaskPath(parsed.pathname);
    if (taskRoute || /^\/(?:calendar\/)?tasks\/?$/.test(parsed.pathname)) {
      const queryTaskId = parsed.searchParams.get('taskId')?.trim() || undefined;
      const legacyId = taskRoute && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(taskRoute.taskSlug)
        ? taskRoute.taskSlug : undefined;
      return { kind: 'task', taskId: taskRoute?.taskId || legacyId || queryTaskId };
    }
  } catch {
    // A malformed encoded slug is untrusted notification data.
    return { kind: 'none' };
  }

  const normalizedPath = parsed.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  if (normalizedPath.length > 0 && isAppView(normalizedPath)) {
    return { kind: 'view', view: normalizedPath };
  }

  if (parsed.pathname.startsWith('/projects')) {
    return { kind: 'view', view: 'projects' };
  }

  if (parsed.pathname.startsWith('/businesses') || parsed.pathname.startsWith('/client-management') || parsed.pathname.startsWith('/workspaces')) {
    return { kind: 'view', view: 'workspaces' };
  }

  if (parsed.pathname.startsWith('/messages')) {
    return { kind: 'view', view: 'messages' };
  }

  if (parsed.pathname.startsWith('/notifications')) {
    return { kind: 'view', view: 'notifications' };
  }

  return { kind: 'none' };
}
